import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  Button,
  Chip,
  Tabs,
  Tab,
  IconButton,
} from '@mui/material';
import {
  MenuBook as ModuleIcon,
  CheckCircle as CompletedIcon,
  Schedule as PendingIcon,
  PlayArrow as StartIcon,
  ArrowBack as BackIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { getTeacherModulesForStudent } from '../../services/lessonService';
import { useAuth } from '../../hooks/useAuth';

interface ModuleAssignment {
  module_id: string;
  title: string;
  subject: string;
  grade_level: string;
  unit_count: number;
  assignment: {
    assignment_id: string;
    status: string;
    due_date: string | null;
    instructions: string | null;
    assigned_at: string;
    updated_at: string;
    completed_at: string | null;
  };
}

export const TeacherModules: React.FC = () => {
  const navigate = useNavigate();
  const { teacherId } = useParams<{ teacherId: string }>();
  const { user } = useAuth();
  const [modules, setModules] = useState<ModuleAssignment[]>([]);
  const [teacherName, setTeacherName] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');

  useEffect(() => {
    if (user && teacherId) {
      loadModules();
    }
  }, [user, teacherId]);

  const loadModules = async () => {
    if (!user || !teacherId) return;

    try {
      setLoading(true);
      setError(null);
      // Use 'me' for student ID - backend will resolve to authenticated user's DynamoDB user_id
      const result = await getTeacherModulesForStudent(teacherId, 'me');
      setModules(result.modules || []);
      setTeacherName(result.teacher_name || 'Teacher');
    } catch (err: any) {
      console.error('Error loading modules:', err);
      setError(err.message || 'Failed to load modules');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'assigned':
        return 'info';
      case 'in_progress':
        return 'warning';
      case 'completed':
        return 'success';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CompletedIcon fontSize="small" />;
      case 'in_progress':
        return <PendingIcon fontSize="small" />;
      default:
        return <AssignmentIcon fontSize="small" />;
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'No due date';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return `Overdue by ${Math.abs(diffDays)} day(s)`;
    } else if (diffDays === 0) {
      return 'Due today';
    } else if (diffDays === 1) {
      return 'Due tomorrow';
    } else {
      return `Due in ${diffDays} day(s)`;
    }
  };

  const isDueSoon = (dueDate: string | null) => {
    if (!dueDate) return false;
    const date = new Date(dueDate);
    const now = new Date();
    const diffTime = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays >= 0 && diffDays <= 3;
  };

  const isOverdue = (dueDate: string | null) => {
    if (!dueDate) return false;
    const date = new Date(dueDate);
    const now = new Date();
    return date < now;
  };

  const filteredModules = modules.filter((module) => {
    if (filterStatus === 'all') return true;
    return module.assignment.status === filterStatus;
  });

  const getStatusCounts = () => {
    return {
      all: modules.length,
      assigned: modules.filter((m) => m.assignment.status === 'assigned').length,
      in_progress: modules.filter((m) => m.assignment.status === 'in_progress').length,
      completed: modules.filter((m) => m.assignment.status === 'completed').length,
    };
  };

  const statusCounts = getStatusCounts();

  if (loading) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton onClick={() => navigate('/student/teachers')} sx={{ mr: 1 }}>
            <BackIcon />
          </IconButton>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            {teacherName}'s Modules
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          View and complete modules assigned by {teacherName}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filter Tabs */}
      <Card sx={{ mb: 3 }}>
        <Tabs
          value={filterStatus}
          onChange={(_, newValue) => setFilterStatus(newValue)}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label={`All (${statusCounts.all})`} value="all" />
          <Tab label={`Assigned (${statusCounts.assigned})`} value="assigned" />
          <Tab label={`In Progress (${statusCounts.in_progress})`} value="in_progress" />
          <Tab label={`Completed (${statusCounts.completed})`} value="completed" />
        </Tabs>
      </Card>

      {/* Modules Grid */}
      {filteredModules.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <ModuleIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {filterStatus === 'all'
                ? 'No modules assigned yet'
                : `No ${filterStatus.replace('_', ' ')} modules`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {filterStatus === 'all'
                ? `${teacherName} will assign modules to you soon`
                : 'Try selecting a different filter'}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {filteredModules.map((module) => (
            <Grid item xs={12} sm={6} md={4} key={module.module_id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  border: isOverdue(module.assignment.due_date) ? '2px solid' : 'none',
                  borderColor: 'error.main',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: 6,
                    transform: 'translateY(-4px)',
                  },
                }}
                onClick={() => navigate(`/student/modules/${module.module_id}`)}
              >
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  {/* Status and Subject */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Chip
                      icon={getStatusIcon(module.assignment.status)}
                      label={module.assignment.status.replace('_', ' ')}
                      color={getStatusColor(module.assignment.status) as any}
                      size="small"
                    />
                    <Chip label={module.subject} size="small" variant="outlined" />
                  </Box>

                  {/* Module Title */}
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    {module.title}
                  </Typography>

                  {/* Grade Level and Unit Count */}
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <Chip label={`Grade ${module.grade_level}`} size="small" variant="outlined" />
                    <Chip
                      label={`${module.unit_count} unit${module.unit_count !== 1 ? 's' : ''}`}
                      size="small"
                      variant="outlined"
                    />
                  </Box>

                  {/* Due Date */}
                  {module.assignment.due_date && (
                    <Box sx={{ mb: 2 }}>
                      <Chip
                        label={formatDate(module.assignment.due_date)}
                        size="small"
                        color={
                          isOverdue(module.assignment.due_date)
                            ? 'error'
                            : isDueSoon(module.assignment.due_date)
                            ? 'warning'
                            : 'default'
                        }
                      />
                    </Box>
                  )}

                  {/* Instructions */}
                  {module.assignment.instructions && (
                    <Box sx={{ mb: 2, flexGrow: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Instructions:
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {module.assignment.instructions.length > 100
                          ? `${module.assignment.instructions.substring(0, 100)}...`
                          : module.assignment.instructions}
                      </Typography>
                    </Box>
                  )}

                  {/* Action Button */}
                  <Button
                    variant={module.assignment.status === 'completed' ? 'outlined' : 'contained'}
                    fullWidth
                    startIcon={
                      module.assignment.status === 'completed' ? <CompletedIcon /> : <StartIcon />
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/student/modules/${module.module_id}`);
                    }}
                    sx={{ mt: 'auto' }}
                  >
                    {module.assignment.status === 'completed'
                      ? 'Review Module'
                      : module.assignment.status === 'in_progress'
                      ? 'Continue'
                      : 'Start Module'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default TeacherModules;
